import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const STATUS_LABELS = {
  interested: { label: 'İlgileniyor', color: 'bg-blue-100 text-blue-800' },
  offer_made: { label: 'Teklif Yapıldı', color: 'bg-yellow-100 text-yellow-800' },
  negotiating: { label: 'Müzakere', color: 'bg-orange-100 text-orange-800' },
  contract_signed: { label: 'Sözleşme İmzalandı', color: 'bg-purple-100 text-purple-800' },
  deposit_paid: { label: 'Depozito Ödendi', color: 'bg-indigo-100 text-indigo-800' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'İptal', color: 'bg-red-100 text-red-800' },
};

const PAGE_SIZE = 20;

export default function AdminInvestmentsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data, count, error } = await adminService.getInvestments({
      status: statusFilter === 'all' ? null : statusFilter,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    if (error) {
      toast({ title: 'Hata', description: 'Yatırımlar yüklenemedi', variant: 'destructive' });
    } else {
      setInvestments(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }, [statusFilter, page, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpdate = async () => {
    if (!selectedInv || !newStatus) return;
    const { error } = await adminService.updateInvestmentStatus(selectedInv.id, newStatus, notes || null);
    if (error) {
      toast({ title: 'Hata', description: 'Güncelleme başarısız', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: 'Yatırım durumu güncellendi' });
      setDialogOpen(false);
      loadData();
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const formatCurrency = (a) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(a || 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Durum Filtrele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Yatırımlar ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : investments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Yatırım bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Yatırımcı</TableHead>
                      <TableHead>Gayrimenkul</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Teklif</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investments.map((inv) => {
                      const st = STATUS_LABELS[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-800' };
                      return (
                        <TableRow key={inv.id}>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/admin/users/${inv.user_id}`)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {inv.user_profile?.full_name || inv.user_id?.slice(0, 8)}
                            </button>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{inv.property?.title_tr || '-'}</TableCell>
                          <TableCell><Badge className={`${st.color} text-xs`}>{st.label}</Badge></TableCell>
                          <TableCell>{inv.offer_amount ? formatCurrency(inv.offer_amount) : '-'}</TableCell>
                          <TableCell className="text-xs">{formatDate(inv.updated_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${inv.user_id}`)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => {
                                setSelectedInv(inv);
                                setNewStatus(inv.status);
                                setNotes(inv.notes || '');
                                setDialogOpen(true);
                              }}>
                                Düzenle
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">{page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} / {totalCount}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm">{page + 1} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yatırım Durumu Güncelle</DialogTitle>
            <DialogDescription>{selectedInv?.property?.title_tr || 'Yatırım'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Durum</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notlar</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Admin notları..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={handleUpdate}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
}
